import React, { useState, useEffect } from 'react';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Heading } from '@/components/Typography';
import Chip from '@/components/Chip';
import { Worker, BypassPreview } from '@/types/onboardingBypass';
import { OnboardingBypassAPI } from '@/services/onboardingBypassAPI';

interface BypassWorkerModalProps {
  worker: Worker | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BypassWorkerModal: React.FC<BypassWorkerModalProps> = ({
  worker,
  open,
  onClose,
  onSuccess,
}) => {
  // Hardcoded available states based on the OnboardingStep format
  const availableStates = [
    {
      id: 'basic_profile',
      title: 'Complete profile',
      status: 'completed',
      duration: 2,
      dependency: null,
      allow_reset: false,
      button_text_id: 'approved',
      status_text_id: null,
    },
    {
      id: 'i9_form',
      title: 'I-9 form',
      status: 'completed',
      duration: 6,
      dependency: ['basic_profile'],
      allow_reset: true,
      button_text_id: 'approved',
      status_text_id: null,
    },
    {
      id: 'background_check',
      title: 'Background Check',
      status: 'completed',
      duration: 10,
      dependency: ['i9_form'],
      allow_reset: false,
      button_text_id: 'approved',
      status_text_id: null,
    },
    {
      id: 'employee_handbook',
      title: 'GravyWork handbook',
      status: 'completed',
      duration: 5,
      dependency: ['background_check'],
      allow_reset: true,
      button_text_id: 'approved',
      status_text_id: null,
    },
    {
      id: 'payment_method',
      title: 'Payment method',
      status: 'completed',
      duration: 2,
      dependency: ['employee_handbook'],
      allow_reset: false,
      button_text_id: 'approved',
      status_text_id: null,
    },
    {
      id: 'ssn_information',
      title: 'SSN/TIN information',
      status: 'completed',
      duration: 2,
      dependency: ['payment_method'],
      allow_reset: false,
      button_text_id: 'approved',
      status_text_id: null,
    },
    {
      id: 'tax_forms',
      title: 'Tax forms',
      status: 'completed',
      duration: 2,
      dependency: ['ssn_information'],
      allow_reset: false,
      button_text_id: 'approved',
      status_text_id: null,
    },
  ];

  const [selectedState, setSelectedState] = useState<string>('');
  const [remarks, setRemarks] = useState<string>('');
  const [preview, setPreview] = useState<BypassPreview | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && worker) {
      setSelectedState('');
      setRemarks('');
      setPreview(null);
      setError(null);
    }
  }, [open, worker]);

  const handleStateChange = async (stateId: string) => {
    setSelectedState(stateId);
    setPreview(null);

    if (stateId && worker) {
      try {
        const response = await OnboardingBypassAPI.previewBypass(
          worker.id,
          stateId,
        );
        if (response.success) {
          setPreview(response.data);
        }
      } catch (error) {
        console.error('Error fetching preview:', error);
      }
    }
  };

  const handleSubmit = async () => {
    if (!worker || !selectedState || !remarks.trim()) {
      setError('Please select a state and provide remarks');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await OnboardingBypassAPI.bypassWorker(worker.id, {
        target_state: selectedState,
        remarks: remarks.trim(),
      });

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        setError(response.error || 'Failed to bypass worker');
      }
    } catch (error) {
      console.error('Error bypassing worker:', error);
      setError('Failed to bypass worker');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !worker) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          width: '90%',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Stack gap={16} vertical>
          {/* Header */}
          <Stack gap={16} justify="apart" align="center">
            <Heading>Bypass Worker: {worker.name}</Heading>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </Stack>

          {/* Target State Selection */}
          <Card title="Select Target State">
            <Card.Section>
              <Stack gap={12} vertical>
                {availableStates.map((state) => (
                  <div
                    key={state.id}
                    style={{
                      padding: '12px',
                      border:
                        selectedState === state.id
                          ? '2px solid #007bff'
                          : '1px solid #e1e5e9',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      backgroundColor:
                        selectedState === state.id ? '#f8f9ff' : 'white',
                    }}
                    onClick={() => handleStateChange(state.id)}
                  >
                    <Stack gap={8} vertical>
                      <Stack gap={8} align="center">
                        <input
                          type="radio"
                          name="targetState"
                          value={state.id}
                          checked={selectedState === state.id}
                          onChange={() => handleStateChange(state.id)}
                          style={{ marginRight: '8px' }}
                        />
                        <Stack gap={4} vertical style={{ flex: 1 }}>
                          <Body weight="medium">{state.title}</Body>
                        </Stack>
                      </Stack>
                      <Stack gap={8}>
                        {state.dependency && state.dependency.length > 0 && (
                          <div style={{ fontSize: '12px', color: '#6c757d' }}>
                            <strong>Dependencies:</strong>{' '}
                            {state.dependency.join(', ')}
                          </div>
                        )}
                      </Stack>
                      {(state as any).actions && (
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 500 }}>
                            Actions:
                          </div>
                          <div style={{ marginTop: '4px' }}>
                            {(state as any).actions.map(
                              (action: string, index: number) => (
                                <div
                                  key={index}
                                  style={{ fontSize: '11px', color: '#6c757d' }}
                                >
                                  {action}
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    </Stack>
                  </div>
                ))}
              </Stack>
            </Card.Section>
          </Card>

          {/* Preview */}
          {preview && (
            <Card title="Preview Changes">
              <Card.Section>
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e1e5e9',
                    borderRadius: '6px',
                  }}
                >
                  <Stack gap={8} vertical>
                    <Body>
                      <strong>Steps to be completed:</strong>{' '}
                      {preview.steps_that_will_be_completed?.length || 0}
                    </Body>
                    <Body>
                      <strong>Steps to reset:</strong>{' '}
                      {preview.changes_summary?.steps_to_reset || 0}
                    </Body>
                    <Body>
                      <strong>Estimated duration:</strong>{' '}
                      {Math.round(preview.estimated_duration || 0)} minutes
                    </Body>
                    <Body>
                      <strong>Final progress:</strong>{' '}
                      {Math.round(
                        preview.changes_summary?.final_progress_percentage || 0,
                      )}
                      %
                    </Body>
                  </Stack>
                </div>
              </Card.Section>
            </Card>
          )}

          {/* Remarks */}
          <Card title="Bypass Remarks">
            <Card.Section>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Please provide a reason for bypassing this worker's onboarding..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '8px',
                  border: '1px solid #e1e5e9',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
                required
              />
              <div style={{ marginTop: '4px' }}>
                <Body color="lighter">
                  This will be recorded in the audit trail
                </Body>
              </div>
            </Card.Section>
          </Card>

          {/* Error Message */}
          {error && (
            <div
              style={{
                padding: '12px',
                backgroundColor: '#fff2f2',
                border: '1px solid #ffcccc',
                borderRadius: '6px',
                color: '#d32f2f',
              }}
            >
              <Body>{error}</Body>
            </div>
          )}

          {/* Actions */}
          <Stack gap={12} justify="end">
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedState || !remarks.trim() || submitting}
              style={{
                padding: '8px 16px',
                backgroundColor:
                  !selectedState || !remarks.trim() || submitting
                    ? '#ccc'
                    : '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor:
                  !selectedState || !remarks.trim() || submitting
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              {submitting ? 'Bypassing...' : 'Bypass Worker'}
            </button>
          </Stack>
        </Stack>
      </div>
    </div>
  );
};

export default BypassWorkerModal;
